import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Drag-to-dismiss for the bottom-drawer variants of Dialog and Sheet.
 *
 * The drag target is the WHOLE PANEL on touch, not just the grabber pill. It used to be
 * the pill alone, which is a ~28px strip — easy to miss, so the usual way out was to give
 * up and tap the backdrop. The rule, matching the app's sheets:
 *
 *   a downward drag dismisses when the content under the finger can't scroll up any
 *   further; otherwise it scrolls. Reaching the top mid-scroll does NOT hand over —
 *   you lift, and the next swipe dismisses. A vertical swipe that NOTHING in the panel
 *   can scroll is held (its touchmoves cancelled) rather than released: iOS Safari would
 *   otherwise chain it to the page behind the drawer, scroll lock or not.
 *
 * Touch only. With a mouse the panel body stays selectable text (dragging a dialog by
 * its own paragraphs would be awful), so a mouse drag is honoured only on the grabber,
 * exactly as before. Backdrop click and Esc are untouched on every device.
 */

/** Past this many pixels the panel is let go. */
const DISMISS_PX = 120;
/** Movement below this is a tap or a hesitation, not yet a drag. */
const SLOP_PX = 6;

/** What sits between `target` and the panel (INCLUDING the panel — a dialog capped to the
 *  viewport with `overflow-y-auto` is its own scroller): the nearest element that can
 *  actually scroll vertically, and whether any native horizontal scroller (overflow-x,
 *  wider than itself) is under the finger. One walk, one computed style per ancestor. */
function scrollersUnder(
  target: EventTarget | null,
  panel: HTMLElement,
): { scroller: HTMLElement | null; sideways: boolean } {
  let scroller: HTMLElement | null = null;
  let sideways = false;
  let el = target instanceof HTMLElement ? target : null;
  while (el) {
    const { overflowY, overflowX } = getComputedStyle(el);
    if (
      !scroller &&
      (overflowY === "auto" || overflowY === "scroll") &&
      el.scrollHeight > el.clientHeight
    ) {
      scroller = el;
    }
    if ((overflowX === "auto" || overflowX === "scroll") && el.scrollWidth > el.clientWidth) {
      sideways = true;
    }
    if (el === panel) break;
    el = el.parentElement;
  }
  return { scroller, sideways };
}

/** Can `scroller` move its content for a swipe of this sign (dy > 0 = finger moving down,
 *  i.e. scrolling towards the top)? Nothing can when there is no scroller at all. */
function canScroll(scroller: HTMLElement | null, dy: number): boolean {
  if (!scroller) return false;
  if (dy > 0) return scroller.scrollTop > 0;
  return scroller.scrollTop + scroller.clientHeight < scroller.scrollHeight - 1;
}

interface DragState {
  startX: number;
  startY: number;
  dy: number;
  /** Pointer is down and could still become a drag. */
  pending: boolean;
  /** Pointer is down and the panel is following it. */
  active: boolean;
  /** Not a drag, but a vertical swipe nothing in the panel can scroll: its touchmoves are
   *  cancelled so the browser can't hand the gesture to the page behind the drawer. */
  hold: boolean;
  scroller: HTMLElement | null;
  /** A native horizontal scroller is under the finger (see scrollersUnder). */
  sideways: boolean;
  /** A touch that began on this panel is still down. */
  tracking: boolean;
}

export function useDrawerDrag(closeRef: React.RefObject<HTMLButtonElement | null>) {
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const drag = React.useRef<DragState>({
    startX: 0,
    startY: 0,
    dy: 0,
    pending: false,
    active: false,
    hold: false,
    scroller: null,
    sideways: false,
    tracking: false,
  });

  const settle = React.useCallback(() => {
    const panel = panelRef.current;
    const s = drag.current;
    // A release that bubbles up from the grabber, or arrives with nothing in flight,
    // must not touch the panel — a second settle here used to wipe the slide-out the
    // first one had just set up.
    if (!panel) return;
    s.hold = false;
    s.tracking = false;
    if (!s.pending && !s.active) return;
    const dismissing = s.active && s.dy > DISMISS_PX;
    s.pending = false;
    s.active = false;
    s.scroller = null;
    if (dismissing) {
      panel.style.transition = "transform 0.2s ease-out";
      panel.style.transform = "translateY(100%)";
      window.setTimeout(() => closeRef.current?.click(), 180);
      // Base UI reuses the popup node across open/close — clear the inline styles once
      // the exit animation is done, or the next open flashes in from translateY(100%).
      window.setTimeout(() => {
        panel.style.transition = "";
        panel.style.transform = "";
      }, 600);
    } else {
      panel.style.transition = "";
      panel.style.transform = "";
    }
  }, [closeRef]);

  /** The browser took the gesture (scroll, system swipe, a call): spring back, never dismiss. */
  const cancel = React.useCallback(() => {
    drag.current.active = false;
    settle();
  }, [settle]);

  /** Shared by the panel (touch) and the grabber (mouse). */
  const begin = React.useCallback(
    (clientX: number, clientY: number, target: EventTarget | null) => {
      const panel = panelRef.current;
      if (!panel) return;
      // Only drag a panel that is actually bottom-anchored. Both Dialog and Sheet become
      // CENTERED at their desktop breakpoint, where sliding down to dismiss makes no sense
      // and the grabber is hidden anyway — a touch device merely wide enough (iPad in
      // landscape) would otherwise still drag it. Measuring beats duplicating the
      // breakpoints here, and it stays right if they ever move. Compared against the
      // layout viewport (`clientHeight`), the same space `getBoundingClientRect` reports
      // in — `innerHeight` disagrees with it under iOS Safari's dynamic toolbar.
      // With the on-screen keyboard up, the drawer is lifted to sit on the keyboard
      // (virtual-keyboard.ts), so its bottom is the VISUAL viewport's bottom, not the
      // layout viewport's. Bailing out there left keyboard-open swipes unhandled, and iOS
      // Safari scrolled the page behind and shoved the sheet around (BDAY-279).
      const vv = window.visualViewport;
      const viewportBottom = Math.min(
        document.documentElement.clientHeight,
        vv ? vv.offsetTop + vv.height : Infinity,
      );
      if (panel.getBoundingClientRect().bottom < viewportBottom - 1) return;
      drag.current = {
        startX: clientX,
        startY: clientY,
        dy: 0,
        pending: true,
        active: false,
        hold: false,
        tracking: true,
        ...scrollersUnder(target, panel),
      };
    },
    [],
  );

  const move = React.useCallback((clientX: number, clientY: number) => {
    const panel = panelRef.current;
    const s = drag.current;
    if (!panel || (!s.pending && !s.active)) return;
    const dy = clientY - s.startY;
    if (!s.active) {
      const dx = Math.abs(clientX - s.startX);
      if (dx === 0 && dy === 0) return;
      // Over a native horizontal scroller (the GIF picker's thumbnail row) its sideways
      // swipe has to reach the browser, so nothing is decided — and nothing cancelled —
      // until the direction settles. Elsewhere the touch is decided on this first move,
      // before the browser can commit it to a native scroll.
      if (s.sideways && Math.abs(dy) < SLOP_PX && dx < SLOP_PX) return;
      // Upward, or the content still has somewhere to scroll — this gesture belongs to
      // the scroller. Give it up for good; the next swipe gets a fresh decision.
      // ...unless nothing under the finger can actually scroll that way. A drawer that
      // fits the screen has no scroller, and one scrolled to its end can't go further:
      // iOS Safari would then chain the swipe to the page behind the (modal, scroll-
      // locked) drawer. Hold the touch instead (BDAY-279). Over a horizontal row this is
      // best effort — a clearly sideways swipe stays the browser's.
      if (dy < 0 || (s.scroller && s.scroller.scrollTop > 0)) {
        s.pending = false;
        if (!(s.sideways && dx > Math.abs(dy)) && !canScroll(s.scroller, dy)) s.hold = true;
        return;
      }
      // Downward with the scroller (if any) at its top: this touch is the panel's. Hold it
      // from the first move for the same reason — the slop below used to let the browser
      // start a page scroll with it.
      s.hold = true;
      if (dy < SLOP_PX && dx < SLOP_PX) return;
      // A mostly-sideways swipe belongs to a horizontal carousel inside the panel (the
      // background picker's swatch row) — give it up, or a slightly-downward swipe across
      // the row grabs the panel and cancels the row's drag.
      if (dx > dy) {
        s.pending = false;
        s.hold = false;
        return;
      }
      s.active = true;
      s.hold = false;
      panel.style.transition = "none";
    }
    s.dy = Math.max(0, dy);
    panel.style.transform = `translateY(${s.dy}px)`;
  }, []);

  // The panel's touch path lives entirely on TOUCH events, and the "is this a drag?"
  // decision is made inside the NON-PASSIVE touchmove itself. The browser commits a touch
  // to a native scroll (and fires pointercancel) on the first touchmove it is allowed to
  // keep, so deciding in a pointermove that may or may not land first is a race we lose
  // on a slow start. Here the first qualifying touchmove is also the one we cancel.
  // Attached from a callback ref rather than an effect: Base UI mounts and reuses the
  // popup node on its own schedule, and a ref callback fires for every node it hands us.
  const touch = React.useRef({
    start: (e: TouchEvent) => {
      const t = e.touches[0];
      if (t) begin(t.clientX, t.clientY, e.target);
    },
    move: (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      move(t.clientX, t.clientY);
      const s = drag.current;
      // A swipe handed to an inner scroller that has hit its end in that direction:
      // iOS Safari chains the rest of it to the page behind (scroll lock or not), so
      // stop it at the edge, the body-scroll-lock way. Sideways swipes stay native.
      const dy = t.clientY - s.startY;
      const chaining =
        s.tracking &&
        !s.active &&
        !s.pending &&
        !!s.scroller &&
        Math.abs(dy) > Math.abs(t.clientX - s.startX) &&
        !canScroll(s.scroller, dy);
      // Once the browser owns a scroll the event is no longer cancelable; don't try.
      if ((s.active || s.hold || chaining) && e.cancelable) e.preventDefault();
    },
    end: () => settle(),
    cancel: () => cancel(),
  }).current;

  const setPanel = React.useCallback(
    (node: HTMLDivElement | null) => {
      const prev = panelRef.current;
      if (prev) {
        prev.removeEventListener("touchstart", touch.start);
        prev.removeEventListener("touchmove", touch.move);
        prev.removeEventListener("touchend", touch.end);
        prev.removeEventListener("touchcancel", touch.cancel);
      }
      panelRef.current = node;
      if (node) {
        node.addEventListener("touchstart", touch.start, { passive: true });
        node.addEventListener("touchmove", touch.move, { passive: false });
        node.addEventListener("touchend", touch.end, { passive: true });
        node.addEventListener("touchcancel", touch.cancel, { passive: true });
      }
    },
    [touch],
  );

  const panelProps = { ref: setPanel };

  /** The grabber is the MOUSE affordance. A touch on it is already handled by the panel's
   *  touch listeners (it is inside the panel), so the pointer path ignores touch here. */
  const grabberProps = {
    onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === "touch") return;
      e.currentTarget.setPointerCapture(e.pointerId);
      // No scroller: the strip itself never scrolls, so a drag here always moves the panel.
      begin(e.clientX, e.clientY, null);
    },
    onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === "touch") return;
      move(e.clientX, e.clientY);
    },
    onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === "touch") return;
      settle();
    },
    onPointerCancel: (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === "touch") return;
      cancel();
    },
  };

  return { panelProps, grabberProps };
}

export function DrawerGrabber({
  grabberProps,
  className,
}: {
  grabberProps: ReturnType<typeof useDrawerDrag>["grabberProps"];
  /** Positioning per surface — e.g. `absolute inset-x-0 top-0` to overlay content. */
  className?: string;
}) {
  return (
    <div
      {...grabberProps}
      aria-hidden
      className={cn(
        "flex cursor-grab touch-none justify-center pb-1.5 pt-2 active:cursor-grabbing",
        className,
      )}
    >
      <div className="h-1.5 w-9 rounded-full bg-border" />
    </div>
  );
}
